<template>
  <v-app class="w-100 mx-auto" id="ans-search-results">

    <!-- Show router view for explorer routes -->
    <router-view v-if="$route.name && ($route.name === 'ConceptDetail' || $route.name === 'ConceptsExplorer')" />
    <!-- Show search layout for main app -->
    <SearchLayout v-else @filters-applied="onFiltersApplied" @filters-reset="onFiltersReset">
      <div id="app-search-result">
        <div class="o-tabs o-tabs--check" style="margin-top: 0;">
          <ul class="js-tablist" data-hx="h2">
            <li v-for="tab in availableTabs" :key="tab.name" class="js-tablist__item">
              <a :href="`#tabs-${tab.name}`" :class="['js-tablist__link', { active: activeTab === tab.name }]"
                :aria-selected="activeTab === tab.name" :tabindex="activeTab === tab.name ? 0 : -1" role="tab"
                :id="`label_tabs-${tab.name}`" :aria-controls="`tabs-${tab.name}`"
                @click.prevent="activeTab = tab.name">
                <img v-if="tab.icon" :src="tab.icon" :alt="tab.label" class="tab-icon" />
                {{ tab.label }}
              </a>
            </li>
          </ul>

          <div class="js-tabcontent--container" style="margin-top: 0;">
            <div :id="`tabs-terminologies`" :class="['js-tabcontent', { active: activeTab === 'terminologies' }]"
              role="tabpanel" aria-labelledby="label_tabs-terminologies" :hidden="activeTab !== 'terminologies'"
              v-if="availableTabs.some(tab => tab.name === 'terminologies')">
              <Terminologies :search="sharedSearch" />
            </div>

            <div :id="`tabs-concepts`" :class="['js-tabcontent', { active: activeTab === 'concepts' }]" role="tabpanel"
              aria-labelledby="label_tabs-concepts" :hidden="activeTab !== 'concepts'"
              v-if="availableTabs.some(tab => tab.name === 'concepts')">
              <div class="concepts-catalog-display">
                <ConceptsCatalog />
              </div>
            </div>

            <div :id="`tabs-alignements`" :class="['js-tabcontent', { active: activeTab === 'alignements' }]"
              role="tabpanel" aria-labelledby="label_tabs-alignements" :hidden="activeTab !== 'alignements'"
              v-if="availableTabs.some(tab => tab.name === 'alignements')">
              <Alignements :search="sharedSearch" />
            </div>

          </div>
        </div>

      </div>
    </SearchLayout>
  </v-app>
</template>

<script>
import SearchLayout from './components/SearchLayout';
import Terminologies from './components/Terminologies';
import ConceptsCatalog from './views/ConceptsCatalog';
import Alignements from './components/Alignements';
import TerminologiesStore from "./terminologies-store";
import ConceptsStore from "./concepts-store";
import AlignmentsStore from "./alignements-store";
import { TAB_TYPES, TAB_CONFIG, urlParamToTabName, tabNameToUrlParam } from '@/enums/tabs';

export default {
  name: 'AppSearchResult',
  components: {
    SearchLayout,
    Terminologies,
    ConceptsCatalog,
    Alignements
  },
  props: {
    search: {
      type: String,
      default: ''
    },
    extendedSearch: {
      type: [String, Boolean],
      default: false
    },
    terminologies: {
      type: [String, Boolean],
      default: false
    },
    concepts: {
      type: [String, Boolean],
      default: false
    }
  },
  data() {
    return {
      urlParameters: {},
      currentUrl: '',
      activeTab: '',
      appliedFilters: null,
      sharedSearch: '',
      versionSelectorDefaultVersionInfo: '1.0.0',
      terminologiesStore: TerminologiesStore,
      conceptsStore: ConceptsStore,
      alignementsStore: AlignmentsStore,
      initialConceptsCount: 0, // Stocke le nombre initial de concepts pour l'onglet
    }
  },

  /**
   * Hook de cycle de vie - Initialisation du composant au montage
   */
  mounted() {
    TerminologiesStore.resetSearchResults();
    ConceptsStore.resetSearchResults();
    this.parseUrlParameters()
    this.currentUrl = window.location.href
    this.sharedSearch = this.propsFromUrl.q;
    this.setInitialActiveTab()
    TerminologiesStore.state.extendedSearch = this.propsFromUrl.extendedSearch;

    const normalizedPathname = window.location.pathname.replace(/\/$/, '');
    const isSearchRootPage = normalizedPathname === '/search';
    if (!isSearchRootPage) {
      return;
    }


    const urlParams = this.getUrlParameters();
    if (urlParams.selectedTypes === undefined || urlParams.selectedTypes.includes('all') || urlParams.selectedTypes.includes('termino')) {
      this.loadTerminologiesData()
    }
    if (urlParams.selectedTypes === undefined || urlParams.selectedTypes.includes('all') || urlParams.selectedTypes.includes('concept')) {
      this.loadConceptsData()
    }
    if (urlParams.selectedTypes === undefined || urlParams.selectedTypes.includes('all') || urlParams.selectedTypes.includes('alignement')) {
      this.loadAlignementsData()
    }
    // Écoute les changements d'URL
    window.addEventListener('popstate', this.handlePopState)
  },

  /**
   * Hook de cycle de vie - Nettoyage avant destruction du composant
   */
  beforeDestroy() {
    window.removeEventListener('popstate', this.handlePopState)
  },

  computed: {
    /**
     * Récupère les props depuis les paramètres d'URL
     * @returns {Object} Objet contenant les props extraites de l'URL
     */
    propsFromUrl() {
      return {
        q: this.urlParameters.search || this.search,
        extendedSearch: this.convertToBoolean(this.urlParameters.extendedSearch) || this.extendedSearch,
        terminologies: this.convertToBoolean(this.urlParameters.terminologies) || this.terminologies,
        concepts: this.convertToBoolean(this.urlParameters.concepts) || this.concepts,
      }
    },

    /**
     * Calcule la liste des onglets disponibles selon les paramètres d'URL
     * @returns {Array} Liste des onglets à afficher avec leurs configurations
     */
    availableTabs() {
      const urlParams = this.getUrlParameters();
      const tabs = [];

      // Déterminer quels tabs sont activés
      const enabledTabs = this.determineEnabledTabs(urlParams.selectedTypes);

      // Ajouter le tab terminologies si activé
      if (enabledTabs.terminologies) {
        tabs.push(this.createTabConfig(TAB_TYPES.TERMINOLOGIES));
      }

      // Ajouter le tab concepts si activé
      if (enabledTabs.concepts) {
        tabs.push(this.createTabConfig(TAB_TYPES.CONCEPTS));
      }

      // Ajouter le tab alignements si activé
      if (enabledTabs.alignements) {
        tabs.push(this.createTabConfig(TAB_TYPES.ALIGNEMENTS));
      }

      // Si aucun tab n'est activé, ajouter les trois par défaut
      if (!enabledTabs.terminologies && !enabledTabs.concepts && !enabledTabs.alignements) {
        tabs.push(
          this.createTabConfig(TAB_TYPES.TERMINOLOGIES),
          this.createTabConfig(TAB_TYPES.CONCEPTS),
          this.createTabConfig(TAB_TYPES.ALIGNEMENTS)
        );
      }

      return tabs;
    }
  },
  methods: {

    /**
     * Détermine quels tabs doivent être activés selon les types sélectionnés
     * @param {Array|string} selectedTypes - Types sélectionnés depuis l'URL
     * @returns {Object} Objet avec les flags d'activation des tabs
     */
    determineEnabledTabs(selectedTypes) {
      // Par défaut, tous les tabs sont activés
      let terminologiesEnabled = true;
      let conceptsEnabled = true;

      // Si selectedTypes est un tableau, vérifier les types présents
      let alignementsEnabled = true;
      if (Array.isArray(selectedTypes)) {
        terminologiesEnabled = selectedTypes.includes(TAB_CONFIG[TAB_TYPES.TERMINOLOGIES].urlParamName);
        conceptsEnabled = selectedTypes.includes(TAB_CONFIG[TAB_TYPES.CONCEPTS].urlParamName);
        alignementsEnabled = selectedTypes.includes(TAB_CONFIG[TAB_TYPES.ALIGNEMENTS].urlParamName);
      }

      return {
        terminologies: terminologiesEnabled,
        concepts: conceptsEnabled,
        alignements: alignementsEnabled
      };
    },

    /**
     * Crée la configuration d'un tab avec son label et son icône
     * @param {string} tabType - Type de tab (utiliser TAB_TYPES)
     * @returns {Object} Configuration du tab
     */
    createTabConfig(tabType) {
      const config = TAB_CONFIG[tabType];

      if (!config) {
        console.error(`Tab type inconnu: ${tabType}`);
        return null;
      }

      let count = 0;

      switch (tabType) {
        case TAB_TYPES.TERMINOLOGIES:
          count = this.terminologiesStore.state.numberOfTerminologies || 0;
          break;
        case TAB_TYPES.CONCEPTS:
          count = this.conceptsStore.state.numberOfConcepts || 0;
          break;
        case TAB_TYPES.ALIGNEMENTS:
          count = this.alignementsStore.state.numberOfAlignments || 0;
          break;
      }

      const label = count > 0 ? `${config.label} (${count})` : config.label;

      return {
        name: config.name,
        label: label,
        icon: config.icon
      };
    },

    /**
     * Récupère les paramètres de recherche depuis l'URL
     * @returns {Object} Objet contenant les paramètres parsés
     */
    getUrlParameters() {
      const urlParams = new URLSearchParams(window.location.search);
      // Récupère le paramètre de langue
      const langParam = urlParams.get('lang');
      let selectedLang;

      if (langParam === null) {
        // Paramètre absent : sélectionner 'fr' par défaut
        selectedLang = 'fr';
      } else if (langParam === '') {
        // Paramètre vide : sélectionner 'all'
        selectedLang = 'all';
      } else {
        // Paramètre avec valeur : utiliser cette valeur (fr, en, etc.)
        selectedLang = langParam;
      }

      // Récupère tous les paramètres de type (peut être multiple)
      const allTypes = urlParams.getAll('type');

      return {
        searchQuery: urlParams.get('search') || '',
        selectedLanguage: selectedLang,
        selectedTypes: allTypes.length > 0 ? allTypes : ['all'],
        extendedSearch: urlParams.get('extendedSearch') === 'true'
      };
    },


    /**
     * Parse les paramètres de l'URL et les stocke dans urlParameters
     */
    parseUrlParameters() {
      const urlParams = new URLSearchParams(window.location.search)
      this.urlParameters = {}

      for (const [key, value] of urlParams.entries()) {
        this.urlParameters[key] = decodeURIComponent(value)
      }
      this.currentUrl = window.location.href
    },

    /**
     * Convertit une valeur en booléen
     * @param {*} value - La valeur à convertir
     * @returns {boolean} La valeur convertie en booléen
     */
    convertToBoolean(value) {
      if (typeof value === 'boolean') return value
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true'
      }
      return false
    },

    /**
     * Définit l'onglet actif initial
     */
    setInitialActiveTab() {
      // Vérifier d'abord le paramètre URL 'tab'
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get('tab');

      if (this.availableTabs.length > 0) {
        // Si le paramètre tab existe dans l'URL, l'utiliser
        if (tabParam) {
          // Convertir le paramètre URL en nom de tab
          const tabValue = urlParamToTabName(tabParam);

          // Si tabValue est valide et existe dans les tabs disponibles
          if (tabValue) {
            const tabExists = this.availableTabs.some(tab => tab.name === tabValue);
            if (tabExists) {
              this.activeTab = tabValue;
              return;
            }
          }
        }

        // Vérifier si l'onglet terminologies existe et si le nombre de terminologies est 0
        const terminologiesTab = this.availableTabs.find(tab => tab.name === TAB_TYPES.TERMINOLOGIES);
        const conceptsTab = this.availableTabs.find(tab => tab.name === TAB_TYPES.CONCEPTS);
        const terminologiesCount = this.terminologiesStore.state.numberOfTerminologies || 0;

        // Si aucun résultat terminologie et l'onglet concepts existe, basculer sur concepts
        if (terminologiesTab && conceptsTab && terminologiesCount === 0) {
          this.activeTab = TAB_TYPES.CONCEPTS;
        } else {
          // Sinon, définir le premier onglet disponible comme actif
          this.activeTab = this.availableTabs[0].name;
        }
      }
    },

    /**
     * Charge les données des terminologies depuis l'API
     */
    async loadTerminologiesData() {
      this.sharedSearch = this.propsFromUrl.q;
      this.terminologiesStore.setExtendedSearch(!!this.propsFromUrl.extendedSearch);
      let endpoint = '/api/terminologies/search';
      let params = {
        'searchedText': this.propsFromUrl.q,
        'adv': this.terminologiesStore.getExtendedSearch(),
        'pagination': TerminologiesStore.state.page,
        'size': TerminologiesStore.state.itemsPerPage,
      };
      TerminologiesStore.getDataFromApi(endpoint, params);
    },

    /**
     * Callback appelé lorsque des filtres sont appliqués
     * @param {Object} filters - Les filtres appliqués
     */
    onFiltersApplied(filters) {
      this.appliedFilters = filters
    },

    /**
     * Callback appelé lorsque les filtres sont réinitialisés
     */
    onFiltersReset() {
      this.appliedFilters = null
      this.appliedFilters = null
    },

    /**
     * Charge les données des concepts depuis l'API
     */
    async loadConceptsData() {
      this.conceptsStore.state.search = this.propsFromUrl.q; // Met à jour la valeur "search" stockée une fois le bouton de recherche cliqué ou la touche ENTRÉE pressée
      this.conceptsStore.state.searchType = this.propsFromUrl.extendedSearch ? 'advanced' : 'classic';
      ConceptsStore.resetSearchResults();
      await ConceptsStore.searchConcepts(false, new Array());

      // Stocker le nombre initial de concepts pour l'affichage fixe dans l'onglet
      if (this.initialConceptsCount === 0 && this.conceptsStore.state.numberOfConcepts > 0) {
        this.initialConceptsCount = this.conceptsStore.state.numberOfConcepts;
      }
    },

    async loadAlignementsData() {
      this.sharedSearch = this.propsFromUrl.q;
      AlignmentsStore.resetSearchResults();
      let endpoint = '/api/alignements/search';
      let params = {
        'searchedText': this.propsFromUrl.q,
        'adv': AlignmentsStore.getExtendedSearch(),
        'pagination': AlignmentsStore.state.page,
        'size': AlignmentsStore.state.itemsPerPage,
      };

      await AlignmentsStore.getDataFromApi(endpoint, params);
    },

    /**
     * Met à jour la page courante des terminologies
     * @param {number} page - Le numéro de page
     */
    updateTerminologiesPage(page) {
      TerminologiesStore.setPage(page)
    },

    /**
     * Met à jour le nombre d'éléments par page pour les terminologies
     * @param {number} itemsPerPage - Le nombre d'éléments par page
     */
    updateTerminologiesItemsPerPage(itemsPerPage) {
      TerminologiesStore.setItemsPerPage(itemsPerPage)
    },

    /**
     * Met à jour le paramètre tab dans l'URL
     * @param {string} tabName - Le nom de l'onglet actif
     */
    updateTabInUrl(tabName) {
      // Ne met à jour l'URL que si on est sur la page de recherche //search
      const pathname = window.location.pathname.replace(/\/$/, ''); // Retire le slash final s'il existe
      if (pathname !== '/search') {
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);
      const tabValue = tabNameToUrlParam(tabName);
      if (tabValue) {
        urlParams.set('tab', tabValue);
        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.pushState({}, '', newUrl);
      }
    },

    /**
     * Gère l'événement popstate (navigation navigateur)
     */
    handlePopState() {
      this.parseUrlParameters();
      this.setInitialActiveTab();
    }
  },
  watch: {
    /**
     * Surveille les changements des onglets disponibles
     */
    availableTabs() {
      // Met à jour l'onglet actif quand les onglets disponibles changent
      this.setInitialActiveTab()
    },

    /**
     * Surveille les changements de l'onglet actif et met à jour l'URL
     */
    activeTab(newTab) {
      if (newTab) {
        this.updateTabInUrl(newTab);
      }
    },


    /**
     * Surveille les changements de la page des terminologies
     */
    'terminologiesStore.page'() {
      // Exécute loadTerminologiesData quand TerminologiesStore.page change
      this.loadTerminologiesData()
    },

    /**
     * Surveille les changements du nombre de terminologies
     */
    'terminologiesStore.state.numberOfTerminologies'(newCount) {
      // Si le nombre de terminologies tombe à 0 et que l'onglet actif est terminologies
      // et que l'onglet concepts existe, basculer automatiquement sur concepts
      if (newCount === 0 && this.activeTab === TAB_TYPES.TERMINOLOGIES) {
        const conceptsTab = this.availableTabs.find(tab => tab.name === TAB_TYPES.CONCEPTS);
        if (conceptsTab) {
          this.activeTab = TAB_TYPES.CONCEPTS;
        }
      }
    }
  }
}
</script>

<style scoped>
#ans-search-results {
  width: 100%;


  background: transparent;

  color: #697097;
  font-family: futura-pt, Arial, sans-serif;
  font-size: 1.8rem;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
  text-align: left;

}

h2 {
  color: #333;
  border-bottom: 2px solid #0073aa;
  padding-bottom: 10px;
}

.no-parameters {
  background-color: #f0f0f0;
  padding: 20px;
  border-radius: 5px;
  text-align: center;
  color: #666;
}

.parameters-display {
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 15px;
  margin: 20px 0;
}

.props-display {
  background-color: #e8f5e8;
  border: 1px solid #4caf50;
  border-radius: 5px;
  padding: 15px;
  margin: 20px 0;
}

.props-display h3 {
  margin-top: 0;
  color: #2e7d32;
}

.parameter-item {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
}

.parameter-item:last-child {
  border-bottom: none;
}

.parameter-item strong {
  color: #0073aa;
  margin-right: 10px;
  min-width: 120px;
}

.parameter-value {
  background-color: #fff;
  padding: 4px 8px;
  border-radius: 3px;
  border: 1px solid #ccc;
  flex: 1;
  font-family: monospace;
}


/* Styles pour les nouveaux tabs utilisant la structure o-tabs */
.o-tabs {
  margin: 20px 0;
}

.js-tablist {
  display: flex;
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
  border-bottom: 2px solid #ddd;
  margin-bottom: 0;
}

.js-tablist__item {
  margin: 0;
  flex: 1;
}

.js-tablist__link {
  display: block;
  padding: 12px 24px;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  text-decoration: none;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.js-tablist__link:hover {
  background-color: #f5f5f5;
  color: #333;
  text-decoration: none;
}

.js-tablist__link.active {
  color: #0073aa;
  border-bottom-color: #0073aa;
  font-weight: bold;
}

.js-tabcontent--container {
  min-height: 200px;
}

.tab-icon {
  width: 40px;
  height: 40px;
  margin-right: 8px;
  vertical-align: middle;
}

/* Masquer les icônes des tabs sur smartphone */
@media (max-width: 768px) {
  .tab-icon {
    display: none;
  }
}

.js-tabcontent {
  display: none;
  background-color: #fafafa;
  border: 1px solid #eee;
  border-radius: 5px;
}

.js-tabcontent.active {
  display: block;
}

.js-tabcontent h4 {
  margin-top: 0;
  color: #0073aa;
}

.js-tabcontent p {
  line-height: 1.6;
}

.applied-filters {
  margin-top: 20px;
  padding: 15px;
  background-color: #e7f3ff;
  border: 1px solid #0073aa;
  border-radius: 5px;
}

.applied-filters h5 {
  margin: 0 0 10px 0;
  color: #0073aa;
}

.applied-filters pre {
  background: white;
  padding: 10px;
  border-radius: 3px;
  border: 1px solid #ddd;
  font-size: 12px;
  overflow-x: auto;
  margin: 0;
}

::v-deep(.v-application--wrap) {
  min-height: inherit;
}

::v-deep(.v-pagination__item),
::v-deep(.v-pagination__navigation) {
  font-size: 18px;
  box-shadow: none;
  border: 0.1rem solid #c5c8da;
  height: 4.4rem;
  width: 4.4rem;
  padding: 0;
}

::v-deep(.v-pagination__item:hover),
::v-deep(.v-pagination__navigation:hover) {
  border-color: var(--v-primary-base);
}


#ans-search-results ::v-deep(.v-sheet.v-card) {
  border-radius: 1.4em;
  box-shadow: none;
  border: .1rem solid #dee0ea;
  color: #697097;
}

#ans-search-results ::v-deep(.v-sheet.v-card .container) {
  padding-left: 2rem;
  padding-right: 2rem;
}

#ans-search-results ::v-deep(.v-input__control) {
  border-radius: 1.4rem;
}

#ans-search-results ::v-deep(.v-input__control .v-input__slot) {
  background: white;
}

#ans-search-results ::v-deep(.v-text-field--outlined fieldset) {
  border-color: #c5c8da;
}

#ans-search-results ::v-deep(.v-input__control label) {
  font-weight: normal;
  font-style: italic;
  color: #697097;
}

#ans-search-results ::v-deep(.v-input__control input) {
  color: #697097;
}

.concepts-catalog-display {
  position: relative;
  height: 90vh;
  width: 100%;
}
</style>
